<script>
import { ref } from 'vue';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { usePolaris } from 'dashboard/composables/usePolaris';
import { useTrack } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';
import { REPLY_EDITOR_MODES, CHAR_LENGTH_WARNING } from './constants';
import NextButton from 'dashboard/components-next/button/Button.vue';
import EditorModeToggle from './EditorModeToggle.vue';
import CopilotMenuBar from './CopilotMenuBar.vue';

export default {
  name: 'ReplyTopPanel',
  components: {
    NextButton,
    EditorModeToggle,
    CopilotMenuBar,
  },
  directives: {
    OnClickOutside: vOnClickOutside,
  },
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    isReplyRestricted: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      default: null,
    },
    isMessageLengthReachingThreshold: {
      type: Boolean,
      default: () => false,
    },
    charactersRemaining: {
      type: Number,
      default: () => 0,
    },
    editorContent: {
      type: String,
      default: undefined,
    },
    hasContent: {
      type: Boolean,
      default: false,
    },
    // The kit Composer's `hint`: the channel the reply goes out through.
    channelHint: {
      type: String,
      default: '',
    },
  },
  emits: ['setReplyMode', 'toggleEditorSize', 'executeCopilotAction'],
  setup(props, { emit }) {
    const setReplyMode = mode => {
      emit('setReplyMode', mode);
    };
    const handleReplyClick = () => {
      if (props.isReplyRestricted) return;
      setReplyMode(REPLY_EDITOR_MODES.REPLY);
    };
    const handleNoteClick = () => {
      setReplyMode(REPLY_EDITOR_MODES.NOTE);
    };
    const handleModeToggle = () => {
      const newMode =
        props.mode === REPLY_EDITOR_MODES.REPLY
          ? REPLY_EDITOR_MODES.NOTE
          : REPLY_EDITOR_MODES.REPLY;
      setReplyMode(newMode);
    };

    const { polarisTasksEnabled } = usePolaris();
    const showCopilotMenu = ref(false);
    const copilotToggleRef = ref(null);

    const handleCopilotAction = (actionKey, data) => {
      emit('executeCopilotAction', actionKey, data || props.editorContent);
      showCopilotMenu.value = false;
    };

    const toggleCopilotMenu = () => {
      const isOpening = !showCopilotMenu.value;
      if (isOpening) {
        useTrack();
      }
      showCopilotMenu.value = isOpening;
    };

    const handleClickOutside = () => {
      showCopilotMenu.value = false;
    };

    const keyboardEvents = {
      'Alt+KeyP': {
        action: () => handleNoteClick(),
        allowOnFocusedInput: false,
      },
      'Alt+KeyL': {
        action: () => handleReplyClick(),
        allowOnFocusedInput: false,
      },
    };
    useKeyboardEvents(keyboardEvents);

    return {
      handleModeToggle,
      handleReplyClick,
      handleNoteClick,
      REPLY_EDITOR_MODES,
      polarisTasksEnabled,
      handleCopilotAction,
      showCopilotMenu,
      copilotToggleRef,
      toggleCopilotMenu,
      handleClickOutside,
    };
  },
  computed: {
    replyButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.REPLY,
      };
    },
    noteButtonClass() {
      return {
        'is-active': this.mode === REPLY_EDITOR_MODES.NOTE,
      };
    },
    charLengthClass() {
      return this.charactersRemaining < 0
        ? 'text-polaris-destructive'
        : 'text-polaris-muted-foreground';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${CHAR_LENGTH_WARNING.NEGATIVE}`
        : `${this.charactersRemaining} ${CHAR_LENGTH_WARNING.UNDER_50}`;
    },
  },
};
</script>

<template>
  <div class="flex w-full items-center justify-between gap-2 px-3 pb-1.5 pt-3">
    <!-- The kit Composer's top addon (InputGroupAddon block-start): the mode on the left, the channel hint on the right. -->
    <EditorModeToggle
      :mode="mode"
      :disabled="disabled"
      :is-reply-restricted="isReplyRestricted"
      @toggle-mode="handleModeToggle"
    />
    <!-- Near the limit the counter takes the channel hint's place; it truncates (full text in title) on a phone. -->
    <div class="flex min-w-0 items-center gap-2">
      <span
        v-if="isMessageLengthReachingThreshold"
        class="min-w-0 truncate text-xs"
        :class="charLengthClass"
        :title="characterLengthWarning"
      >
        {{ characterLengthWarning }}
      </span>
      <span
        v-else-if="channelHint"
        class="min-w-0 truncate text-xs text-polaris-muted-foreground"
      >
        {{ channelHint }}
      </span>
      <div v-if="polarisTasksEnabled" class="flex shrink-0 items-center gap-1">
        <div class="relative">
          <NextButton
            ref="copilotToggleRef"
            v-tooltip.top-end="$t('CONVERSATION.SIDEBAR.COPILOT')"
            ghost
            :disabled="disabled || isEditorDisabled"
            :class="{
              'text-n-violet-9 hover:enabled:!bg-n-violet-3': !showCopilotMenu,
              'text-n-violet-9 bg-n-violet-3': showCopilotMenu,
            }"
            sm
            icon="i-ph-sparkle-fill"
            :aria-label="$t('CONVERSATION.SIDEBAR.COPILOT')"
            @click="toggleCopilotMenu"
          />
          <CopilotMenuBar
            v-if="showCopilotMenu"
            v-on-click-outside="[
              handleClickOutside,
              { ignore: [copilotToggleRef] },
            ]"
            :has-selection="false"
            :has-content="hasContent"
            :conversation-id="conversationId"
            class="ltr:right-0 rtl:left-0 bottom-full mb-2"
            @execute-copilot-action="handleCopilotAction"
          />
        </div>
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.EXPAND_EDITOR')"
          ghost
          class="text-polaris-muted-foreground"
          sm
          icon="i-lucide-maximize-2"
          :aria-label="$t('CONVERSATION.REPLYBOX.EXPAND_EDITOR')"
          @click="$emit('toggleEditorSize')"
        />
      </div>
    </div>
  </div>
</template>
